import { createSelector } from '@reduxjs/toolkit';

import type { CourseModel, CoursesState } from './types';

import type { RootState } from 'Store';

const selectCoursesState = (state: RootState): CoursesState => state.courses;
const getId = (_state: RootState, id: string) => id;
const getParticipantId = (_state: RootState, _id: string, participantId: string) => participantId;

export const selectCourses = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.courses ?? []
);

export const selectAreCoursesLoading = createSelector(
  selectCoursesState,
  (state: CoursesState): boolean => state.areCoursesLoading
);

export const selectIsCoursesLoaded = createSelector(
  selectCoursesState,
  (state: CoursesState): boolean => state.isCoursesLoaded
);

export const selectAreTagsLoading = createSelector(
  selectCoursesState,
  (state: CoursesState): boolean => state.areTagsLoading
);

export const selectCoursesViewMode = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.courseListViewMode
);

export const selectIsCourseCreating = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.isCourseCreating
);

export const selectIsCourseUpdating = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.isCourseUpdating
);

export const selectIsCourseForEditLoading = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.isCourseForEditLoading
);

export const selectCourseForEditError = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.courseForEditError
);

export const selectIsFreshStartCreating = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.isFreshStartCreating
);

export const selectHasCoursesData = createSelector(selectCourses, courses => courses.length > 0);

export const selectTestCourse = createSelector(
  selectCoursesState,
  (state: CoursesState): CourseModel | undefined | null =>
    state.courses?.find((course: CourseModel) => course.isTest) ?? null
);

export const selectFilteredCourses = createSelector(selectCourses, courses => courses);

export const selectIsCourseManagementBusy = createSelector(
  [selectIsCourseCreating, selectIsCourseUpdating],
  (isCreating, isUpdating) => isCreating || isUpdating
);

export const selectCoursesIds = createSelector(selectCourses, (courses): string[] =>
  courses.map((course: CourseModel) => course.id)
);

export const selectCourse = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): CourseModel | undefined | null =>
    state.courses?.find((course: CourseModel) => course.id === id) ?? null
);

export const selectCourseParticipants = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string) => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.participants ?? [];
  }
);

export const selectCourseMyDesktop = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string) => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.myDesktop ?? null;
  }
);

export const selectCourseTrainerDesktop = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string) => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.trainerDesktop ?? null;
  }
);

export const selectCourseFreshStartDesktop = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string) => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.freshStartDesktop ?? null;
  }
);

export const selectAreParticipantsLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.areParticipantsLoading === true;
  }
);

export const selectAreParticipantsLoaded = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.areParticipantsLoaded === true;
  }
);

export const selectIsJoinCodeLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.isJoinCodeLoading === true;
  }
);

export const selectIsJoinCodeLoaded = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.isJoinCodeLoaded === true;
  }
);

export const selectIsMyDesktopLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isMyDesktopLoading
);

export const selectIsMyDesktopLoaded = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.isMyDesktopLoaded === true;
  }
);

export const selectIsTrainerDesktopLoaded = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.isTrainerDesktopLoaded === true;
  }
);

export const selectIsTrainerDesktopLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isTrainerDesktopLoading
);

export const selectIsFreshStartDesktopLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isFreshStartDesktopLoading
);

export const selectIsFreshStartDesktopLoaded = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.isFreshStartDesktopLoaded === true;
  }
);

export const selectIsAnyDesktopLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    if (!course) {
      return false;
    }

    return (
      course.isMyDesktopLoading === true ||
      course.isTrainerDesktopLoading === true ||
      course.isFreshStartDesktopLoading === true
    );
  }
);

export const selectIsAnyDesktopPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    if (!course) {
      return false;
    }

    return (
      course.isMyDesktopStartPending === true ||
      course.isTrainerDesktopStartPending === true ||
      course.isFreshStartDesktopStartPending === true ||
      course.isMyDesktopStopPending === true ||
      course.isFreshStartDesktopStopPending === true ||
      course.isMyDesktopDeletePending === true ||
      course.isFreshStartDesktopDeletePending === true
    );
  }
);

export const selectCourseExists = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)
);

export const selectHasCourseDesktops = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return !!(course?.myDesktop ?? course?.trainerDesktop ?? course?.freshStartDesktop);
  }
);

export const selectHasCourseParticipants = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    const participants = course?.participants ?? [];
    return participants.length > 0;
  }
);

export const selectIsCourseReady = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return !!course?.isFreshStartMachineReadyToClone;
  }
);

export const selectCanPreviewCourse = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return !!course?.whetherTrainerAllowedPreview;
  }
);

export const selectHasMyDesktop = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return !!course?.myDesktop;
  }
);

export const selectHasTrainerDesktop = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return !!course?.trainerDesktop;
  }
);

export const selectHasFreshStartDesktop = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return !!course?.freshStartDesktop;
  }
);

export const selectIsMyDesktopRunning = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.myDesktop?.isRunning === true;
  }
);

export const selectIsTrainerDesktopRunning = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.trainerDesktop?.isRunning === true;
  }
);

export const selectIsFreshStartDesktopRunning = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.freshStartDesktop?.isRunning === true;
  }
);

export const selectIsFreshStartDesktopStartPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)
      ?.isFreshStartDesktopStartPending
);

export const selectIsFreshStartDesktopStopPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isFreshStartDesktopStopPending
);

export const selectIsFreshStartDesktopDeletePending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)
      ?.isFreshStartDesktopDeletePending
);

export const selectFreshStartDesktopLink = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): string | undefined | null => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.freshStartDesktop?.link ?? null;
  }
);

export const selectIsMyDesktopStartPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isMyDesktopStartPending
);

export const selectIsMyDesktopStopPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isMyDesktopStopPending
);

export const selectIsMyDesktopDeletePending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.isMyDesktopDeletePending === true;
  }
);

export const selectMyDesktopLink = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): string | undefined | null => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    return course?.myDesktop?.link ?? null;
  }
);

export const selectIsParticipantDesktopStartPending = (courseId: string, participantId: string) =>
  createSelector([selectCoursesState], (state: CoursesState): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === courseId);
    const participant = course?.participants?.find(p => p.id === participantId);
    return participant?.isDesktopStartPending === true;
  });

export const selectIsParticipantDesktopStopPending = (courseId: string, participantId: string) =>
  createSelector([selectCoursesState], (state: CoursesState): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === courseId);
    const participant = course?.participants?.find(p => p.id === participantId);
    return participant?.isDesktopStopPending === true;
  });

export const selectIsParticipantDesktopDeletePending = (courseId: string, participantId: string) =>
  createSelector([selectCoursesState], (state: CoursesState): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === courseId);
    const participant = course?.participants?.find(p => p.id === participantId);
    return participant?.isDesktopDeletePending === true;
  });

export const selectParticipantDesktopViewLink = createSelector(
  [selectCoursesState, getId, getParticipantId],
  (state: CoursesState, courseId: string, participantId: string): string | undefined | null => {
    const course = state.courses?.find((course: CourseModel) => course.id === courseId);
    const participant = course?.participants?.find(p => p.id === participantId);
    return participant?.desktop?.viewLink ?? null;
  }
);

export const selectIsTrainerDesktopStartPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isTrainerDesktopStartPending
);

export const selectIsTrainerDesktopStopPending = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean =>
    !!state.courses?.find((course: CourseModel) => course.id === id)?.isTrainerDesktopStopPending
);

export const selectIsCreateCourseSuccess = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.isCreateCourseSuccess
);

export const selectIsUpdateCourseSuccess = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.isUpdateCourseSuccess
);

export const selectCreateCourseResult = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.createCourseResult
);

export const selectUpdateCourseResult = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.updateCourseResult
);

export const selectStandalones = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.standalones
);

export const selectAreStandalonesLoading = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.areStandalonesLoading
);

export const selectFreshStartServersForCourse = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, courseId: string) => state.freshStartServers?.[courseId] ?? null
);

export const selectAreFreshStartServersLoading = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, courseId: string) => state.areFreshStartServersLoading?.[courseId] ?? false
);

export const selectOsTemplatesOfServer = createSelector(
  [selectCoursesState, (_: RootState, serverName: string) => serverName],
  (state: CoursesState, serverName: string) => {
    const templates = state.osTemplates?.[serverName] ?? [];
    return { osTemplateNames: templates };
  }
);

export const selectAreOsTemplatesLoading = createSelector(
  [selectCoursesState, (_: RootState, serverName: string) => serverName],
  (state: CoursesState, serverName: string) => state.areOsTemplatesLoading?.[serverName] ?? false
);

export const selectOsTemplatesError = createSelector(
  [selectCoursesState, (_: RootState, serverName: string) => serverName],
  (state: CoursesState, serverName: string) => state.osTemplatesErrors?.[serverName] ?? false
);

export const selectCoursesError = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.coursesError
);

export const selectIsCourseDataFullyLoaded = createSelector(
  [selectCoursesState, getId],
  (state: CoursesState, id: string): boolean => {
    const course = state.courses?.find((course: CourseModel) => course.id === id);
    if (!course) {
      return false;
    }

    return !!(
      course.isJoinCodeLoaded &&
      course.isMyDesktopLoaded &&
      course.isFreshStartDesktopLoaded &&
      course.areParticipantsLoaded
    );
  }
);

export const selectCoursesPagination = createSelector(
  selectCoursesState,
  (state: CoursesState) => state.pagination
);
